import {
  clearCookiesOnInvalidRefreshToken,
  decodeWorkosAccessTokenPayload,
  getUserId,
  initWorkosToken,
  refreshWorkosToken,
} from '../lib/workos'
import { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'

export interface AuthState {
  initialized: boolean
  session: Session | null
  user: User | null
  workosSessionId: string | null
  setInitialized: (initialized: boolean) => void
  setSession: (session: Session | null) => void
  setWorkosSessionId: (sessionId: string | null) => void
  setWorkosAccessToken: (token: string | null, expiresAt?: number | null) => boolean
  initSession: () => Promise<Session | null>
  refreshWorkosToken: () => Promise<boolean>
  reset: () => void
}

const baseState = {
  initialized: false,
  session: null,
  user: null,
  workosSessionId: null,
} satisfies Pick<AuthState, 'initialized' | 'session' | 'user' | 'workosSessionId'>

let workosAccessToken: string | null = null

const buildSession = (options: {
  userId: string
  accessToken: string
  expiresAt: number | null
  email: string
}): Session => {
  const now = Date.now()
  const expiresAtSeconds = options.expiresAt
    ? Math.floor(options.expiresAt / 1000)
    : Math.floor(now / 1000) + 3600
  const expiresIn = options.expiresAt ? Math.max(0, Math.floor((options.expiresAt - now) / 1000)) : 3600

  return {
    user: {
      id: options.userId,
      email: options.email,
      app_metadata: {},
      user_metadata: {},
      aud: 'authenticated',
      created_at: new Date().toISOString(),
    },
    access_token: options.accessToken,
    token_type: 'bearer',
    expires_in: expiresIn,
    expires_at: expiresAtSeconds,
    refresh_token: '',
  }
}

const useAuth = create<AuthState>()((set, get) => ({
  ...baseState,
  setInitialized: initialized => set({ initialized }),
  setSession: session => set({ session, user: session?.user ?? null }),
  setWorkosSessionId: workosSessionId => set({ workosSessionId }),
  setWorkosAccessToken: (token, expiresAt) => {
    if (!token) {
      workosAccessToken = null
      get().setSession(null)
      return true
    }

    const userId = getUserId()
    if (!userId) {
      console.error('[Auth] Missing nobgp-user-id cookie - auth callback may have failed')
      workosAccessToken = null
      get().setSession(null)
      return false
    }

    workosAccessToken = token

    const tokenPayload = decodeWorkosAccessTokenPayload(token)
    const tokenExpiresAt =
      expiresAt ?? (typeof tokenPayload?.exp === 'number' ? tokenPayload.exp * 1000 : null)
    const email = typeof tokenPayload?.email === 'string' ? tokenPayload.email : ''

    // Extract session ID from JWT and store it
    const sid = typeof tokenPayload?.sid === 'string' ? tokenPayload.sid : null
    if (sid) {
      get().setWorkosSessionId(sid)
    }

    const session = buildSession({
      userId,
      accessToken: token,
      expiresAt: tokenExpiresAt,
      email,
    })

    get().setSession(session)
    return true
  },
  initSession: async () => {
    try {
      // If token is already in memory (e.g. just set by AuthCallbackPage),
      // skip the round-trip to the router
      const existingSession = get().session
      if (workosAccessToken && existingSession) {
        console.log('[Auth] Session already initialized from callback')
        return existingSession
      }

      const tokenResult = await initWorkosToken()

      if (!tokenResult?.accessToken) {
        console.log('[Auth] No token found, user needs to sign in')
        return null
      }

      const success = get().setWorkosAccessToken(tokenResult.accessToken, tokenResult.expiresAt)
      if (!success) {
        return null
      }

      console.log('[Auth] Session initialized from cookie')

      const session = get().session
      if (!session) {
        console.warn('[Auth] Token exists but session not available')
        return null
      }

      return session
    } finally {
      set({ initialized: true })
    }
  },
  refreshWorkosToken: async () => {
    const result = await refreshWorkosToken()

    if (result.success && result.accessToken && typeof result.expiresIn === 'number') {
      // Use token data directly from POST response (no extra GET request)
      const now = Date.now()
      const expiresAt = now + result.expiresIn * 1000

      get().setWorkosAccessToken(result.accessToken, expiresAt)

      console.log('[Auth] Token refreshed successfully')
      return true
    }

    console.warn('[Auth] Token refresh failed:', result.error)
    const isPermanentFailure = result.error === 'invalid_refresh_token' || result.error === 'missing_refresh_token'

    // Only clear auth state for definitive auth failures.
    // Transient failures (network/server) should keep the last token/session in memory.
    if (isPermanentFailure) {
      get().setWorkosAccessToken(null)
      await clearCookiesOnInvalidRefreshToken(result.error)
    }
    return false
  },
  reset: () => {
    set({ ...baseState })
    workosAccessToken = null
  },
}))

export const setWorkosAccessToken = (token: string | null, expiresAt?: number | null) =>
  useAuth.getState().setWorkosAccessToken(token, expiresAt)

export const getWorkosAccessToken = () => {
  if (workosAccessToken) return workosAccessToken

  // Defensive fallback in case module state is lost while Zustand state persists.
  const sessionToken = useAuth.getState().session?.access_token ?? null
  if (sessionToken) {
    workosAccessToken = sessionToken
  }

  return sessionToken
}

export default useAuth
